// Looking for an online war (docs/ARCHITECTURE.md §15): a quick match against whoever is searching on the player's
// continent, a new challenge (the link to send a friend, and the wait for them), or a friend's challenge being accepted.
// The search runs while the screen is up and is canceled when it is left; a found match goes to the app flow, which
// opens the war room.

import './matchmaking.css'
import type { MatchAssignment, SearchPhase } from '@taxwar/online'
import type { Screen, ScreenContext } from '../../app/screen'
import { button, canvasHost, el, TextSlot } from '../../hud/dom'
import { applyScreenTheme } from '../../hud/theme'
import { MatchmakingError, MatchmakingFailure, challengeLink, findWarMatch, type EdgeOrigin, type OnlineWarRequest } from '../../online'
import type { VisualSettings } from '../../render/visualSettings'
import type { TuningLink } from '../../tuning/link'

interface EntryCopy {
  readonly title: string
  readonly body: string
  readonly waiting: string
}

const ENTRY_COPY: { readonly [K in OnlineWarRequest['entry']['kind']]: EntryCopy } = {
  quick: {
    title: 'Searching for a rival',
    body: 'Anyone on your continent looking for a war is matched with you. Diplomacy has been ruled out as too expensive.',
    waiting: 'Scanning the continent for a willing belligerent',
  },
  challenge: {
    title: 'Challenge a friend',
    body: 'Send them this link. The war starts when they open it and accept. The link is good for one war.',
    waiting: 'Waiting for your friend to accept',
  },
  accept: {
    title: 'Accepting the challenge',
    body: 'Your acceptance has been stamped, countersigned and faxed to the war office.',
    waiting: 'Contacting the challenger',
  },
}

const FAILURE_COPY: Readonly<Record<MatchmakingFailure, string>> = {
  [MatchmakingFailure.timeout]: 'Nobody turned up in time. Try again, or challenge a friend directly.',
  [MatchmakingFailure.busy]: 'The war office is at capacity. Try again in a moment.',
  [MatchmakingFailure.roomUnavailable]: 'The war room could not be set up. Try again.',
  [MatchmakingFailure.canceled]: 'The search was canceled.',
  [MatchmakingFailure.edgeRefused]: 'The war office refused the request.',
  [MatchmakingFailure.inviteTaken]: 'That challenge has already been accepted, or it has expired. Ask for a new link.',
  [MatchmakingFailure.connectionLost]: 'Lost contact with the war office. Check your connection and try again.',
  [MatchmakingFailure.protocol]: 'The war office sent something unreadable. Try again.',
}

const MATCHED_TEXT = 'Rival found. Opening the war room…'
const COPIED_LABEL = 'Copied'
const COPY_LABEL = 'Copy link'
const MS_PER_SECOND = 1000

export class MatchmakingScreen implements Screen {
  private readonly overlay: HTMLElement
  private readonly tuning: TuningLink
  private readonly statusLine: HTMLElement
  private readonly status: TextSlot
  private readonly elapsed: TextSlot
  private readonly search = new AbortController()
  private readonly startedAt = performance.now()
  private shownSeconds = -1
  private settled = false

  constructor(
    context: ScreenContext,
    request: OnlineWarRequest,
    edge: EdgeOrigin,
    private readonly onMatched: (assignment: MatchAssignment) => void,
    onBack: () => void,
  ) {
    const signal = context.signal
    const copy = ENTRY_COPY[request.entry.kind]
    this.overlay = el('div', 'tw-screen tw-matchmaking')
    this.tuning = context.tuning
    applyScreenTheme(this.overlay, context.tuning, context.settings)
    const panel = el('main', 'tw-matchmaking__panel tw-panel')
    this.statusLine = el('p', 'tw-matchmaking__status')
    const statusText = el('span', 'tw-matchmaking__status-text')
    this.status = new TextSlot(statusText)
    const elapsedText = el('span', 'tw-matchmaking__elapsed tw-dim')
    this.elapsed = new TextSlot(elapsedText)
    this.statusLine.append(statusText, elapsedText)
    panel.append(el('h1', 'tw-matchmaking__title', copy.title), el('p', 'tw-dim', copy.body))
    if (request.entry.kind === 'challenge') panel.append(this.linkBox(challengeLink(location.origin, request.entry.code), signal))
    const back = button('tw-button', 'Cancel')
    back.addEventListener('click', onBack, { signal })
    const actions = el('footer', 'tw-matchmaking__actions')
    actions.append(back)
    panel.append(this.statusLine, actions)
    this.overlay.append(panel)
    canvasHost(context.renderer).append(this.overlay)
    this.status.set(`${copy.waiting}…`)

    signal.addEventListener('abort', () => this.search.abort(new DOMException('the matchmaking screen was left', 'AbortError')), { once: true })
    context.addUpdater(this.tick)
    findWarMatch(edge, request, this.onPhase, this.search.signal).then(this.matched, (error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') return
      if (!(error instanceof MatchmakingError)) throw error
      this.fail(FAILURE_COPY[error.failure], back)
    })
  }

  resize(): void {
    // The overlay is laid out by CSS.
  }

  applySettings(settings: VisualSettings): void {
    applyScreenTheme(this.overlay, this.tuning, settings)
  }

  dispose(): void {
    this.overlay.remove()
  }

  private linkBox(link: string, signal: AbortSignal): HTMLElement {
    const box = el('div', 'tw-matchmaking__link')
    const field = el('input', 'tw-matchmaking__link-field')
    field.readOnly = true
    field.value = link
    field.setAttribute('aria-label', 'Challenge link')
    field.addEventListener('focus', () => field.select(), { signal })
    const copyButton = button('tw-button tw-button--primary', COPY_LABEL)
    copyButton.addEventListener('click', () => {
      void navigator.clipboard.writeText(link).then(() => {
        copyButton.textContent = COPIED_LABEL
      })
    }, { signal })
    box.append(field, copyButton)
    return box
  }

  private readonly onPhase = (phase: SearchPhase): void => {
    if (phase.kind === 'allocating') this.status.set('Match found. Requisitioning a war room…')
  }

  private readonly matched = (assignment: MatchAssignment): void => {
    if (this.search.signal.aborted) return
    this.settled = true
    this.status.set(MATCHED_TEXT)
    this.onMatched(assignment)
  }

  private fail(text: string, back: HTMLButtonElement): void {
    this.settled = true
    this.status.set(text)
    this.statusLine.classList.add('is-error')
    this.elapsed.set('')
    back.textContent = 'Back'
  }

  /** Per frame: the whole seconds waited so far, written only when the second changes. */
  private readonly tick = (): void => {
    if (this.settled) return
    const seconds = Math.floor((performance.now() - this.startedAt) / MS_PER_SECOND)
    if (seconds === this.shownSeconds) return
    this.shownSeconds = seconds
    this.elapsed.set(` ${seconds} s`)
  }
}
